import { IApiErrorData } from "api/axios/types";
import { IBusinessDetails } from "api/business-user-management/types";
import {
  IApproveOrRejectBusinessSectionPayload,
  IApproveOrRejectBusinessSectionResponse,
  TBusinessSectionType,
} from "api/compliance/types";
import { useBusinessCompliance } from "contexts/business-compliance-context";
import { BusinessComplianceQueryKeys } from "contexts/business-compliance-context/consts";
import { capitalizeFirstLetter } from "utils";

import { customErrorToast, customSuccessToast } from "ui/custom-toast";

import { useMutateData } from "./useMutateData";

export type TBusinessSectionTitle =
  | "Business Details"
  | "Business Documents"
  | "Business Address"
  | "Director's Information";

export const businessSectionType: Record<
  TBusinessSectionTitle,
  TBusinessSectionType
> = {
  "Business Documents": "business-documents",
  "Business Address": "business-documents", // business address for unregistered business is the same as business documents
  "Business Details": "business-information",
  "Director's Information": "director-information",
};

export const businessSectionTypeToLabelMap: Record<
  TBusinessSectionType,
  TBusinessSectionTitle
> = {
  "business-documents": "Business Documents",
  "business-information": "Business Details",
  "director-information": "Director's Information",
  "business-address": "Business Address",
};

export interface IUseApproveOrRejectBusinessSectionArgs {
  businessId: string;
  businessSectionTitle: TBusinessSectionTitle;
  handleToggleApproveOrRejectModal: () => void;
}

export interface IUseApproveOrRejectBusinessSectionReturnData {
  approveBusinessSection: () => Promise<void>;
  rejectBusinessSection: (reason: string) => Promise<void>;
  isApprovingOrRejecting: boolean;
}

export const useApproveOrRejectBusinessSection = (
  args: IUseApproveOrRejectBusinessSectionArgs
): IUseApproveOrRejectBusinessSectionReturnData => {
  const { businessId, businessSectionTitle, handleToggleApproveOrRejectModal } =
    args;
  const {
    selectedBusiness,
    setSelectedBusiness,
    // complianceTrails,
    // setComplianceTrails,
    handleGetComplianceTrails,
  } = useBusinessCompliance();

  const {
    mutateAsync: handleApproveOrRejectSection,
    isPending: isApprovingOrRejecting,
  } = useMutateData<
    IApproveOrRejectBusinessSectionPayload,
    IApproveOrRejectBusinessSectionResponse
  >([BusinessComplianceQueryKeys.BUSINESS_DETAILS], {
    onSuccess: async (data, payload) => {
      customSuccessToast({
        title: `${businessSectionTitle} ${capitalizeFirstLetter(payload.data.status)}`,
        message: data.message,
      });

      let updatedBusiness: IBusinessDetails | null = null;
      if (selectedBusiness && data.company) {
        updatedBusiness = {
          ...selectedBusiness,
          business: {
            ...selectedBusiness?.business,
            companyData: {
              ...selectedBusiness?.business?.companyData,
              ...data.company,
              businessDirectors:
                selectedBusiness?.business?.companyData?.businessDirectors ||
                [],
            },
          },
        };
      }

      if (updatedBusiness) {
        setSelectedBusiness(updatedBusiness);
      }
      // setComplianceTrails(data?.company?.complianceTrails || complianceTrails);
      handleToggleApproveOrRejectModal();
      handleGetComplianceTrails(businessId);
    },
    onError(error) {
      const err = error as unknown as IApiErrorData;
      const message = err.message;
      customErrorToast({ message });
    },
  });

  const approveBusinessSection = async () => {
    try {
      await handleApproveOrRejectSection({
        apiRoute: `/admin/compliance-section-update/${businessId}`,
        data: {
          type: businessSectionType[businessSectionTitle],
          status: "approved",
          reason: "",
        },
        method: "POST",
        module: "EARNIPAY_STG",
        useEncryption: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const rejectBusinessSection = async (reason: string) => {
    try {
      await handleApproveOrRejectSection({
        apiRoute: `/admin/compliance-section-update/${businessId}`,
        data: {
          type: businessSectionType[businessSectionTitle],
          status: "rejected",
          reason,
        },
        method: "POST",
        module: "EARNIPAY_STG",
        useEncryption: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    approveBusinessSection,
    rejectBusinessSection,
    isApprovingOrRejecting,
  };
};
